/**
 * Daily org-wide hours fixture for the Dashboard time-series chart (task 157).
 *
 * Shape: one row per civil day in the active mockup window, with separate
 * billable / non-billable hour buckets. The chart consumes this directly
 * via `useDashboardData.useDashboardDailyStats()` in mockup mode; live
 * mode is gated to an empty array until a backend `/dashboard/daily-stats`
 * endpoint exists (out of scope for this task — see task 157 §Out of scope).
 *
 * Per web/frontend/CLAUDE.md §Fixture-data pattern, every date here is
 * derived from FROZEN_NOW so tests stay deterministic and the mockup
 * window does not slide forward when wall-clock time advances.
 */

import { FROZEN_NOW } from '@/lib/frozenTime';

export interface DailyStat {
  /** Civil date in YYYY-MM-DD. */
  date: string;
  /** Hours classified as billable on this day. */
  billableHours: number;
  /** Hours classified as non-billable (general / break / unclassified). */
  nonBillableHours: number;
}

// Anchor at midnight UTC of the FROZEN_NOW day so every fixture date is
// exactly N civil days back from the same reference.
const FROZEN_DAY_MS = (() => {
  const d = new Date(FROZEN_NOW);
  d.setUTCHours(0, 0, 0, 0);
  return d.getTime();
})();

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Synthesised hours per day. The numbers are hand-shaped (not random) so
 * the chart in screenshots and tests has a recognisable weekly cadence —
 * weekdays carry billable load, weekends are quieter, and there is a
 * gentle upward trend across the 30-day window.
 *
 * Indexes count back from today (FROZEN_NOW): index 0 is "today minus 29
 * days", index 29 is "today". The 30-day shape matches the "last 30"
 * default date-range preset.
 */
const SHAPE: ReadonlyArray<readonly [number, number]> = [
  // [billable, nonBillable] hour pairs, oldest first.
  [22.4, 4.1],
  [25.1, 3.8],
  [27.8, 5.2],
  [24.6, 4.4],
  [26.2, 4.0],
  [4.8, 1.2], // Sat
  [3.1, 0.9], // Sun
  [28.5, 5.7],
  [30.2, 4.8],
  [29.7, 5.1],
  [31.4, 6.0],
  [28.9, 4.6],
  [5.5, 1.4], // Sat
  [2.7, 0.6], // Sun
  [32.1, 5.9],
  [33.8, 6.2],
  [30.5, 5.4],
  [34.2, 6.7],
  [31.7, 5.5],
  [6.1, 1.3], // Sat
  [3.4, 1.0], // Sun
  [35.6, 6.4],
  [33.2, 5.9],
  [36.8, 7.1],
  [34.5, 6.5],
  [32.9, 5.8],
  [5.7, 1.5], // Sat
  [3.2, 0.8], // Sun
  [37.3, 6.8],
  [38.1, 7.2],
];

export const DASHBOARD_DAILY_STATS: readonly DailyStat[] = SHAPE.map(
  ([billableHours, nonBillableHours], idx) => {
    const dayOffset = SHAPE.length - 1 - idx; // 29..0 (oldest..newest)
    const date = new Date(FROZEN_DAY_MS - dayOffset * DAY_MS).toISOString().slice(0, 10);
    return { date, billableHours, nonBillableHours };
  },
);
